import { useEffect } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FieldInput, FieldTextarea, FieldSelect, FieldSwitch, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";

const CATEGORY = [
    ["TECHNOLOGY", "Tecnologia"],
    ["PROGRAMMING", "Programacion"],
    ["DATA", "Datos"],
    ["DEVOPS", "DevOps"],
    ["SECURITY", "Seguridad"],
    ["DESIGN", "Diseno"],
    ["BUSINESS", "Negocios"],
    ["PRODUCTIVITY", "Productividad"],
    ["SOFT_SKILLS", "Habilidades blandas"],
    ["ACADEMIC", "Academico / general"],
    ["OTHER", "Otro"],
].map(([value, label]) => ({ value, label }));

const STATUS = [
    ["IDEA", "Idea / todavia sin armar"],
    ["PLANNING", "Planificando"],
    ["READY", "Preparado"],
    ["ACTIVE", "En curso"],
    ["PAUSED", "Pausado"],
    ["COMPLETED", "Finalizado"],
].map(([value, label]) => ({ value, label }));

const LEVEL = [
    ["BEGINNER", "Inicial"],
    ["BASIC", "Basico"],
    ["INTERMEDIATE", "Intermedio"],
    ["ADVANCED", "Avanzado"],
    ["ALL_LEVELS", "Mixto / varios niveles"],
].map(([value, label]) => ({ value, label }));

const MODALITY = [
    ["IN_PERSON", "Presencial"],
    ["LIVE_ONLINE", "Online en vivo"],
    ["ONLINE", "Online"],
    ["HYBRID", "Hibrido"],
    ["SELF_PACED", "Material asincronico"],
    ["WORKSHOP", "Taller / workshop"],
    ["BOOTCAMP", "Intensivo / bootcamp"],
].map(([value, label]) => ({ value, label }));

const EMPTY = {
    title: "",
    description: "",
    category: "TECHNOLOGY",
    status: "PLANNING",
    level: "INTERMEDIATE",
    modality: "IN_PERSON",
    platform: "",
    language: "es",
    audience: "",
    objectives: "",
    prerequisites: "",
    startDate: "",
    endDate: "",
    estimatedHours: 0,
    color: "#e83a52",
    isFavorite: false,
};

const COURSE_FIELDS = [
    "title", "description", "category", "status", "level", "modality", "platform",
    "language", "audience", "objectives", "prerequisites", "startDate", "endDate",
    "estimatedHours", "color", "isFavorite",
];

const pickCourseFields = (source) => {
    const input = source && typeof source === "object" ? source : {};
    return COURSE_FIELDS.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(input, key)) acc[key] = input[key];
        return acc;
    }, {});
};

const valuesOf = (course) => course ? {
    ...EMPTY,
    ...pickCourseFields(course),
    startDate: course.startDate || "",
    endDate: course.endDate || "",
} : { ...EMPTY };

const optionValues = (rows) => rows.map((row) => row.value);
const oneOf = (rows, message) => (value) => (
    !value || optionValues(rows).includes(value) ? null : message
);
const hoursRule = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return "Las horas estimadas deben ser un numero entero";
    if (parsed < 0 || parsed > 10000) return "Las horas estimadas deben estar entre 0 y 10000";
    return null;
};

export const CourseFormModal = ({ isOpen, onClose, course = null, onSave, isLoading = false }) => {
    const FORM = useForm({
        initialValues: EMPTY,
        schema: {
            title: { required: true, minLength: 2, maxLength: 180 },
            description: { maxLength: 5000 },
            category: { custom: oneOf(CATEGORY, "Elegi un area valida") },
            status: { custom: oneOf(STATUS, "Elegi un estado valido") },
            level: { custom: oneOf(LEVEL, "Elegi un nivel valido") },
            modality: { custom: oneOf(MODALITY, "Elegi un contexto valido") },
            platform: { maxLength: 120 },
            language: { maxLength: 16 },
            audience: { maxLength: 5000 },
            objectives: { maxLength: 10000 },
            prerequisites: { maxLength: 10000 },
            startDate: { date: true },
            endDate: { date: true },
            estimatedHours: { custom: hoursRule },
            color: { maxLength: 32 },
        },
        onSubmit: async (values) => {
            const payload = {
                ...pickCourseFields(values),
                title: String(values.title || "").trim(),
                description: String(values.description || "").trim() || undefined,
                platform: String(values.platform || "").trim() || undefined,
                language: String(values.language || "es").trim() || "es",
                audience: String(values.audience || "").trim() || undefined,
                objectives: String(values.objectives || "").trim() || undefined,
                prerequisites: String(values.prerequisites || "").trim() || undefined,
                startDate: values.startDate || null,
                endDate: values.endDate || null,
                estimatedHours: Number(values.estimatedHours || 0),
            };

            const result = await onSave?.(payload);

            if (result === false || result?.success === false) {
                if (result && typeof result === "object") FORM.applyApiError(result);
                return result;
            }

            onClose?.();
            return result;
        },
    });

    useEffect(() => {
        if (isOpen) FORM.reset(valuesOf(course));
    }, [isOpen, course]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            isDirty={FORM.isDirty}
            title={course?.id ? "Editar datos del curso" : "Nuevo curso"}
            description="Este curso es un cuaderno privado. Aca definis el contexto; los apuntes, tareas e itinerario se cargan adentro."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={isLoading || FORM.isSubmitting}>
                        {course?.id ? "Guardar cambios" : "Crear curso"}
                    </AppButton>
                </>
            }
        >
            {FORM.serverError && (
                <div className="LStudio__academic_form_error_summary" role="alert" aria-live="assertive">
                    <strong>No se pudo guardar.</strong>
                    <span>{FORM.serverError}</span>
                    {FORM.requestId && <small>Referencia tecnica: {FORM.requestId}</small>}
                </div>
            )}

            <div className="LStudio__course_form_wrap">
                <div className="LStudio__app_priority_toggle">
                    <FieldSwitch id="isFavorite" name="isFavorite" label="Favorito" text="Fijar este curso"
                        isChecked={!!FORM.values.isFavorite}
                        onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
                </div>
                <section>
                    <div className="LStudio__course_form_section_head">
                        <h3>Identificacion</h3>
                        <p>Nombre y contexto general para reconocer rapido este cuaderno docente.</p>
                    </div>
                    <FormGrid columns={3}>
                        <FieldInput {...FORM.field("title")} label="Nombre del curso" placeholder="Ej: Ruby on Rails - Backend" span={2} />
                        <FieldSelect {...FORM.field("status")} label="Estado" options={STATUS} />
                        <FieldSelect {...FORM.field("category")} label="Area" options={CATEGORY} />
                        <FieldSelect {...FORM.field("level")} label="Nivel" options={LEVEL} />
                        <FieldSelect {...FORM.field("modality")} label="Contexto" options={MODALITY} />
                        <FieldInput {...FORM.field("platform")} label="Institucion / plataforma" placeholder="Ej: Instituto, Udemy, Meet..." span={2} />
                        <FieldInput {...FORM.field("language")} label="Idioma" placeholder="es" />
                        <FieldTextarea {...FORM.field("description")} label="Descripcion personal" rows={4} span={3}
                            placeholder="De que trata, como queres encararlo, que no queres olvidar..." />
                    </FormGrid>
                </section>

                <section>
                    <div className="LStudio__course_form_section_head">
                        <h3>Plan docente</h3>
                        <p>Informacion privada para pensar el curso. No es una ficha comercial ni una landing.</p>
                    </div>
                    <FormGrid columns={3}>
                        <FieldTextarea {...FORM.field("audience")} label="A quien se lo voy a ensenar" rows={4} span={3}
                            placeholder="Perfil de alumnos, conocimientos esperados, contexto..." />
                        <FieldTextarea {...FORM.field("objectives")} label="Que quiero que aprendan" rows={4} span={3}
                            placeholder="Objetivos que quiero cubrir durante el curso." />
                        <FieldTextarea {...FORM.field("prerequisites")} label="Que deberian saber antes" rows={4} span={3}
                            placeholder="Conceptos previos o temas que conviene repasar." />
                        <FieldInput {...FORM.field("startDate")} type="date" label="Inicio" />
                        <FieldInput {...FORM.field("endDate")} type="date" label="Fin estimado" />
                        <FieldInput {...FORM.field("estimatedHours")} type="number" min="0" label="Horas estimadas" />
                    </FormGrid>
                </section>
            </div>
        </AppModal>
    );
};
